import { type CloudinaryTransformOptions } from '#app/utilities/cloudinaryTransforms/types.js';
import { type Area, type MediaSize } from 'react-easy-crop';

type Point = [x: number, y: number];

/**
 * Takes data from react-easy-crop and calculates the needed
 * Cloudinary transforms for a given cropping from react-easy-crop.
 *
 * Use output in combination with `buildCloudinaryTransforms` to build a transformation string.
 * @param {object} imageData - An object containing input data for cropping an image.
 * @param {MediaSize} imageData.mediaSize - An object containing the naturalHeight and naturalWidth of the image. See `onMediaLoaded` in react-easy-crop.
 * @param {Area} imageData.croppedAreaData - An object containing the x, y, width, and height of the cropped area. See `onCropComplete` in react-easy-crop. You want the second argument, `croppedAreaPixels`.
 * @returns {CloudinaryTransformOptions[]} - An object containing the crop, height, width, x, and y values to pass to a Cloudinary transformation utility.
 */
export const getCloudinaryTransformsForCropping = ({
  croppedAreaData,
  mediaSize,
}: {
  croppedAreaData: Area;
  mediaSize: MediaSize;
}): readonly CloudinaryTransformOptions[] => {
  const { naturalHeight, naturalWidth } = mediaSize;

  const {
    height: canvasHeight,
    width: canvasWidth,
    x: originalCropX,
    y: originalCropY,
  } = croppedAreaData;

  const calculatedZoom: number = Number.parseFloat(
    (
      Math.min(naturalHeight, naturalHeight) /
      Math.min(canvasHeight, canvasWidth)
    ).toFixed(2),
  );

  const zoomGreaterOrEqualToOne = calculatedZoom >= 1;

  // Important to invert the X and Y values coming from the cropper croppedAreaData.
  const cropTopLeftPoint: Point = [originalCropX * -1, originalCropY * -1];

  const cropCenterPoint: Point = [
    naturalWidth / 2 + cropTopLeftPoint[0],
    naturalHeight / 2 + cropTopLeftPoint[1],
  ];

  const canvasCenterPoint: Point = [canvasWidth / 2, canvasHeight / 2];

  const crop: 'crop' | 'lpad' | 'mpad' = zoomGreaterOrEqualToOne
    ? // Also test the bottom and right edges
      cropTopLeftPoint[0] > 0 ||
      cropTopLeftPoint[1] > 0 ||
      cropTopLeftPoint[0] < (naturalWidth - canvasWidth) * -1 ||
      cropTopLeftPoint[1] < (naturalHeight - canvasHeight) * -1
      ? 'mpad'
      : 'crop'
    : 'lpad';

  const x =
    crop === 'crop'
      ? originalCropX
      : Math.round(cropCenterPoint[0] - canvasCenterPoint[0]);
  const y =
    crop === 'crop'
      ? originalCropY
      : Math.round(cropCenterPoint[1] - canvasCenterPoint[1]);

  const initialTransform: Required<
    Pick<CloudinaryTransformOptions, 'crop' | 'height' | 'width' | 'x' | 'y'>
  > = {
    crop,
    height: canvasHeight,
    width: canvasWidth,
    // Avoid -0 values.
    x: x || 0,
    y: y || 0,
  };

  if (crop === 'mpad') {
    return [
      initialTransform,
      {
        crop: 'crop',
        height: canvasHeight,
        width: canvasWidth,
      },
    ];
  }

  return [initialTransform];
};
